import { leagueFetch } from '@leagueplatform/league-fetch';
import { DATA_PROCESSOR_ENDPOINT } from '../constants/masonry-node-api';
import type {
  DataProcessorField,
  DataProcessorResponse,
} from '../types/data-processor';
import { getMasonryRequestHeaders } from './get-masonry-request-headers';

const formatFields = (fields: DataProcessorField[]) =>
  fields.map((field) => {
    const mappedField: {
      id: string;
      value: any;
      metadata?: Record<string, string>;
    } = {
      id: field.id,
      value: field.value,
    };

    if (field.metadata?.length) {
      mappedField.metadata = field.metadata.reduce<Record<string, string>>(
        (acc, { key, value }) => {
          acc[key] = value;
          return acc;
        },
        {},
      );
    }

    return mappedField;
  });

export const postDataProcessor = async ({
  dataProcessorId,
  fields,
}: {
  dataProcessorId: string;
  fields?: DataProcessorField[];
}): Promise<DataProcessorResponse> => {
  const attributes: { fields?: ReturnType<typeof formatFields> } = {};

  if (fields?.length) {
    attributes.fields = formatFields(fields);
  }

  const body = {
    data: {
      type: 'data-processor-invocation',
      attributes,
    },
  };

  const headers = getMasonryRequestHeaders();

  const response = await leagueFetch(
    `${DATA_PROCESSOR_ENDPOINT}/${dataProcessorId}`,
    {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      allowUnauthenticated: true,
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const error = await response.json();
      message = error.message || message;
    } catch {
      // non-JSON or empty error body — use status code
    }
    throw new Error(`Data Processor Action Error: ${message}`);
  }

  const text = await response.text();

  if (!text) {
    return { status: 'success' };
  }

  const responseJson: {
    data: { attributes: DataProcessorResponse };
  } = JSON.parse(text);
  return responseJson.data.attributes;
};
